// One documented voice for the sentences the customer reads when something goes wrong or a list
// comes back empty.
//
// LSP-19485 — error sentences: the app shipped at least six openers ("Something went wrong",
// "Failed to", "Unable to", "We're sorry", "Oops", "An error occurred") for the same event, so
// Projects and Settings described one failed load two different ways. Every load/save failure now
// builds its sentence here: one opener, one retry hint.
//
// LSP-19486 — empty lists: "No results" read six different ways, including Vuetify's framework
// default "No data available." One phrasing now covers every list.
//
// LSP-21412 — a failure the customer did not cause also tells them how to reach us, so every
// sentence built here ends with the support address. AGENTS.md → "User-facing error messages".

import { extractHttpStatus } from "@/helpers/freshEntityRetry";

const RETRY_HINT = "Please try again.";

export const SHARE_PERMISSION_MESSAGE = "You do not have permission to share. Ask the owner to share it directly.";

/**
 * The support address for customer-facing copy. Read at CALL time, never at module scope:
 * `window.env` is only populated after main.ts's start() fetches /config/env.json, and the EU
 * deploy points it at a different inbox.
 */
export function supportEmail(): string {
	return window.env?.VITE_SUPPORT_EMAIL || "help@liveswitch.com";
}

/** "If it keeps happening, email us at <address>." — the reach-us sentence for failures on our side. */
export function supportHint(): string {
	return `If it keeps happening, email us at ${supportEmail()}.`;
}

/** "Something went wrong. Please try again. If it keeps happening, email us at …" */
export function genericFailure(): string {
	return `Something went wrong. ${RETRY_HINT} ${supportHint()}`;
}

function trimTrailingPeriods(value: string): string {
	return value.replace(/\.+\s*$/, "");
}

/**
 * "We couldn't <action>. Please try again. If it keeps happening, email us at …" — the single
 * sentence pattern for a failed operation. `action` is a verb phrase naming what the customer was
 * trying to do ("create the project").
 */
export function couldNot(action: string): string {
	const subject = trimTrailingPeriods(action ?? "").trim();
	if (!subject) return genericFailure();
	return `We couldn't ${subject}. ${RETRY_HINT} ${supportHint()}`;
}

/** Recovery copy shared by project banners and disabled actions. */
export function projectAccessFailureMessage(): string {
	return `We couldn't check your access. Click Retry. ${supportHint()}`;
}

/** Offline detail pages wait for the browser to reconnect instead of offering a dead Retry action. */
export function offlineMessage(): string {
	return "You're offline. We'll continue when you're back online.";
}

/** "We couldn't load <thing>. Please try again." for a failed read. */
export function couldNotLoad(thing: string): string {
	const subject = trimTrailingPeriods(thing ?? "").trim();
	if (!subject) return genericFailure();
	return couldNot(`load ${subject}`);
}

/** "We couldn't save <thing>. Please try again." for a failed write. */
export function couldNotSave(thing: string): string {
	const subject = trimTrailingPeriods(thing ?? "").trim();
	if (!subject) return genericFailure();
	return couldNot(`save ${subject}`);
}

/**
 * LSP-21867 / LSP-21868: "We couldn't find that <thing>. It may have been deleted." — for an old or
 * mistyped link to a record that no longer exists. No retry hint: trying again cannot bring it back.
 */
export function couldNotFind(thing: string): string {
	const subject = trimTrailingPeriods(thing ?? "").trim();
	if (!subject) return genericFailure();
	return `We couldn't find that ${subject}. It may have been deleted.`;
}

/**
 * True for a load that failed because the record does not exist: a 404 in any of the API client's error
 * shapes, or the transport's own "Resource not found" (what a malformed id gets back).
 */
export function isNotFoundError(error: unknown): boolean {
	if (extractHttpStatus(error) === 404) return true;
	const message = (error as { message?: unknown } | null | undefined)?.message;
	return typeof message === "string" && message.trim().toLowerCase() === "resource not found";
}

/**
 * LSP-19486: "No results" read six different ways — Vuetify's framework default "No data
 * available.", "No contacts found.", "No forms match your search.", an icon plus "No community
 * templates found / Try adjusting your search or filters." One phrasing now covers every list.
 *
 * @param things plural noun for the list's contents, lower case ("projects", "contacts")
 * @param query the active search term, if the list is empty because of a search
 * @param options.filtersApplied LSP-20939: the list is empty because filters narrowed it. It is then
 *   neither "empty" nor a failed search alone, so the message names the filters.
 */
export function listEmptyMessage(things: string, query?: string | null, options: { filtersApplied?: boolean } = {}): string {
	const noun = (things ?? "").trim() || "results";
	const searchTerm = (query ?? "").trim();
	if (options.filtersApplied) {
		return searchTerm ? `No ${noun} match “${searchTerm}” with these filters` : `No ${noun} match these filters`;
	}
	return searchTerm ? `No ${noun} match “${searchTerm}”` : `No ${noun} yet`;
}

/**
 * LSP-21412: a save blocked by field validation. The customer has not done anything wrong — a field
 * just needs finishing — so the sentence never scolds ("Oops… fix them") and points at the
 * highlighted fields.
 */
const FIELDS_NEED_ATTENTION = "A few fields need your attention. Check the highlighted fields, then save again.";

/**
 * `window.open` returned null — the browser blocked the popup, or the tab limit is exhausted
 * (indistinguishable, and identical in effect). Names the recovery the customer can act on.
 *
 * LSP-19467: the re-authentication path used to append the URL it failed to open, putting a raw
 * link in a toast. The URL belongs in the log, never in the sentence.
 */
const POPUP_BLOCKED = "Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.";

export { FIELDS_NEED_ATTENTION, POPUP_BLOCKED, RETRY_HINT };
